import React, {useEffect} from "react";
import {
    IonBadge,
    IonButton,
    IonCard, IonCardContent, IonCardHeader, IonCardTitle,
    IonContent, IonIcon, IonInput, IonItem, IonLabel,
    IonLoading,
    IonPage, IonSelect, IonSelectOption, useIonModal
} from "@ionic/react";
import './AcaPipeline.css';
import {
    arrowDownOutline, arrowDownSharp, arrowUpOutline, arrowUpSharp,
    documentOutline,
    documentSharp, trashOutline, trashSharp
} from "ionicons/icons";
import {useHistory} from "react-router";
import AcaChart, {CarrierSummaryChart} from "../aca/components/AcaChart";
import {useMutation} from "@apollo/client";
import {ACA_GET_PIPELINE_INFO, ACA_GET_PIPELINE_STAGES} from "../../schemas/aca";
import {RecentOpportunityChanges} from "./types";
import {formatDatetime, formatStatus} from "../../utils";
import useAuth from "../../hooks/useAuth";
import {AcaHeader} from "./AcaHeader";
import PagingButtons from "../../components/PagingButtons";

let filterTimeout: null | NodeJS.Timeout = null;
const AcaPipeline: React.FC<any> = () => {
    const {claims } = useAuth();
    const history = useHistory();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);

    const [acaGetPipelineInfoMutation] = useMutation(ACA_GET_PIPELINE_INFO);
    const [acaGetPipelineStagesMutation] = useMutation(ACA_GET_PIPELINE_STAGES);
    const [opportunities, setOpportunities] = React.useState<RecentOpportunityChanges[] | undefined>(undefined);
    const [chart3, setChart3] = React.useState<CarrierSummaryChart | undefined>(undefined);
    const [stagesSummary, setStagesSummary] = React.useState<{name: string, count: number, id: number}[] | undefined>();
    const [contactStateSummary, setContactStateSummary] = React.useState<{stageName: string, stageId: number, state: string, count: number}[] | undefined>();
    const [selectedStage, setSelectedStage] = React.useState<number>(1);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [numPages, setNumPages] = React.useState<number>(1);
    const [orderColumn, setOrderColumn] = React.useState<string>('CREATED_DATETIME');
    const [orderDirection, setOrderDirection] = React.useState<string>('DESC');
    const [totalCount, setTotalCount] = React.useState<number | undefined>(undefined);
    const [filterColumn, setFilterColumn] = React.useState<string | undefined>(undefined);
    const [filterValue, setFilterValue] = React.useState<string | undefined>(undefined);

    const [availableStages, setAvailableStages] = React.useState<{id: number, name: string, position: number, enrolled: boolean, failed: boolean}[] | undefined>(undefined);
    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    const loadData = async (id?: number, page?: number, column?: string | undefined, direction?: string |  undefined, fcolumn?:string|null|undefined, fvalue?:string|null|undefined) => {
        setIsLoading(true);
        if (!page) {
            page = 1;
        }
        if (!id) {
            id = 1;
        } else if (typeof id !== 'number') {
            id = Number(id);
        }
        if (!column) {
            column = orderColumn;
        }
        if (!direction) {
            direction = orderDirection;
        }
        if (!fcolumn && fcolumn !== null) {
            fcolumn = filterColumn;
        }
        if (!fvalue && fvalue !== null) {
            fvalue = filterValue;
        }

        setSelectedStage(id);
        let lookupResult = await acaGetPipelineInfoMutation({
            variables: {
                stageId: id,
                pageSize: 50,
                currentPage: page,
                orderColumn: column,
                orderDirection: direction,
                filterColumn: fcolumn,
                filterValue: fvalue
            }
        });

        if (lookupResult.data?.acaGetPipelineInfo) {
            setNumPages(lookupResult.data.acaGetPipelineInfo.pagination.numOfPages);
            setTotalCount(lookupResult.data.acaGetPipelineInfo.pagination.totalCount);
            setOpportunities(lookupResult.data.acaGetPipelineInfo.opportunities);
            setStagesSummary(lookupResult.data.acaGetPipelineInfo.stagesSummary);

            setContactStateSummary(lookupResult.data.acaGetPipelineInfo.contactStateSummary);
            setChart3({
                title: 'Opportunity Stages',
                subtitle: '',
                data: {
                    labels: lookupResult.data.acaGetPipelineInfo.stagesSummary.map((item: {
                        count: number,
                        name: string
                    }) => item.name),
                    datasets: [
                        {
                            data: lookupResult.data.acaGetPipelineInfo.stagesSummary.map((item: {
                                count: number,
                                name: string
                            }) => Math.round(item.count)),
                            backgroundColor: lookupResult.data.acaGetPipelineInfo.stagesSummary.map((item: {
                                count: number,
                                name: string
                            }, index: number) => 'rgba(22, 143, 164, ' + ((index + 1) * .18) + ')'),
                            borderColor: lookupResult.data.acaGetPipelineInfo.stagesSummary.map((item: {
                                count: number,
                                name: string
                            }) => 'rgba(22, 143, 164)'),
                            borderWidth: 1,
                            dataVisibility: new Array(lookupResult.data.acaGetPipelineInfo.stagesSummary.length).fill(true),
                        },
                    ]
                },
                options: {

                    type: 'doughnut',
                    plugins: {
                        tooltip: {
                            titleFont: {
                                size: 14
                            },
                            bodyFont: {
                                size: 12
                            },
                        },
                        legend: {
                            display: true,
                            responsive: true,
                            position: "bottom",
                            labels: {
                                boxWidth: 36,
                                padding: 5,
                                font: {
                                    size: 12
                                },
                            },
                            align: "center",
                        },
                    }
                }
            });
        }
        const stagesResult = await acaGetPipelineStagesMutation({variables: {pipelineId: 1}});

        if (!stagesResult.data.acaGetPipelineStages.success) {
            alert(stagesResult.data.acaGetPipelineStages.error);
        } else {
            setAvailableStages(stagesResult.data.acaGetPipelineStages.stages);
        }
        setIsLoading(false);
    };
    useEffect(() => {
        // check for roles
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        }
        loadData(selectedStage, 1);
    }, [selectedStage]);

    const handlePipelineSelect = async(id: number) => {
        setSelectedStage(id);

    };
    const toggleOrdering = async (column: string) => {
        if (column != orderColumn) {
            setOrderColumn(column);
            setOrderDirection('DESC');
            await loadData(selectedStage, 1, column, 'DESC');
        } else {
            if (orderDirection === 'DESC') {
                setOrderDirection('ASC');
                await loadData(selectedStage, 1, column, 'ASC');
            } else {
                setOrderDirection('DESC');
                await loadData(selectedStage, 1, column, 'DESC');
            }
        }
    };

    const showDetails = async (id: number) => {
        history.push('/aca/view-opportunity/'+id);
    };
    const changePage = async (id: number) => {
        setCurrentPage(Number(id));
        await loadData(selectedStage, id);
    }

    const handleFilter = async (filterColumn: string, filterValue: string | null | undefined)=> {
        if (filterColumn && filterValue) {
            setFilterColumn(filterColumn);
            setFilterValue(filterValue);
            if (filterTimeout) {
                clearTimeout(filterTimeout);
            }

            filterTimeout = setTimeout(loadData, 1000, selectedStage, 1);
        }
    };
    const resetFilters = async () => {
        setFilterColumn(undefined);
        setFilterValue(undefined);
        loadData( selectedStage, 1, undefined, undefined, null, null);
    };

    return (
        <IonPage>

            <AcaHeader pageTitle={"ACA Pipeline"}></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-pipeline">
                <div style={{padding: '10px'}}>
                    <p style={{paddingLeft: '10px'}}><b>Your User Role:</b> {
                        manager && <>HC Manager</>
                    }{
                        !manager && tier2 && <>HC Tier 2</>
                    }{
                        !manager && !tier2 && tier1 && <>HC Tier 1</>
                    }{
                        !manager && !tier2 && !tier1 && <>Viewing User</>
                    }</p>

                    <h1>Opportunity Pipeline</h1>


                    {chart3 && chart3.data.datasets.length > 0 &&

                        <AcaChart type="doughnut" key="chart1" chart={chart3} style={{
                            width: '300px',
                            float: 'left'
                        }}></AcaChart>
                    }
                    <IonCard style={{display: 'inline-block', width: '400px', float: 'left'}}>
                        <IonCardHeader>
                            <IonCardTitle>Pipeline Summary</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            {stagesSummary && stagesSummary.map((stage, index) => {
                                return (
                                    <p key={'stageSummary_'+index}><a style={{cursor: 'pointer'}} onClick={() => loadData(stage.id)}>{stage.name}</a> ({stage.count})</p>
                                )
                            })}
                        </IonCardContent>
                    </IonCard>
                    <IonCard style={{display: 'inline-block', width: '400px', float: 'left'}}>
                        <IonCardHeader>
                            <IonCardTitle>Pipeline Summary By State</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            {contactStateSummary && contactStateSummary.map((stage, index) => {
                                if (contactStateSummary[index - 1] && contactStateSummary[index - 1].stageId === stage.stageId) {
                                    // other rows
                                    return (
                                        <div key={'cs_' + index}><IonButton size="small" className="xs-button" style={{cursor: 'pointer', float: 'right',display: 'inline-block',  marginRight:'5px'}} onClick={() => loadData(stage.stageId, 1, undefined, undefined, 'ADDRESS_STATE', stage.state)}>{stage.state} ({stage.count})</IonButton></div>
                                    );
                                } else {
                                    // first row
                                    return (
                                        <div key={'cs_' + index}>
                                            <div style={{clear: 'both'}}></div>
                                            <hr style={index !== 0 ? {height: '2px', boxShadow: '2px 2px 2px rgba(0,0,0,0.4)'} : {display: 'none'}}/>
                                            <a style={{
                                                cursor: 'pointer',
                                                float: 'left'
                                            }} onClick={() => loadData(stage.stageId)}>{stage.stageName}</a>
                                            <IonButton size="small" className="xs-button" style={{
                                                display: 'inline-block',
                                                cursor: 'pointer',
                                                float: 'right',
                                                marginRight: '5px'
                                            }} onClick={() => loadData(stage.stageId, 1, undefined, undefined, 'ADDRESS_STATE', stage.state)}>{stage.state} ({stage.count})</IonButton>
                                        </div>
                                    );
                                }
                            })}
                            <div style={{clear: 'both'}}></div>

                        </IonCardContent>
                    </IonCard>

                    <div style={{clear: 'both'}}></div>
                    <IonItem style={{width: '400px'}}>
                        <IonLabel>Selected Pipeline Stage</IonLabel>
                        <IonSelect value={selectedStage} onIonChange={(e) => handlePipelineSelect(e.detail.value)}>
                            {availableStages && availableStages.map((stage, index) => {
                                return (
                                    <IonSelectOption value={stage.id} key={'stage_'+index}>{stage.name}</IonSelectOption>
                                )
                            })}
                        </IonSelect>
                    </IonItem>
                    <p><b>Displaying Pipeline Stage:</b> New</p>


                    {filterColumn &&
                        <>Filters: <IonBadge style={{padding: '5px'}}>{formatStatus(filterColumn)}: {filterValue} <a onClick={()=>resetFilters()} style={{cursor: 'pointer', color: 'white'}}><IonIcon md={trashOutline} ios={trashSharp}></IonIcon></a></IonBadge></>
                    }


                    {(!opportunities || opportunities.length == 0) &&
                        <div style={{marginTop: '40px'}}>No Opportunities</div>
                    }
                    {opportunities && opportunities.length > 0 &&
                        <>
                            <p><b>Number of results:</b> {opportunities.length}</p>
                        {numPages &&
                            <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                        }
                        <table style={{width: '100%', marginTop: '20px'}} className="aca-healthcare">

                            <thead>
                            <tr>
                                <th>Opp ID</th>
                                <th><span onClick={() => toggleOrdering('FIRST_NAME')} style={{cursor: 'pointer'}}>First Name {orderColumn === 'FIRST_NAME' && orderDirection === 'DESC' &&
                                    <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                }
                                    {orderColumn === 'FIRST_NAME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</span>

                                </th>
                                <th><span onClick={() => toggleOrdering('LAST_NAME')} style={{cursor: 'pointer'}}>Last
                                    Name {orderColumn === 'LAST_NAME' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'LAST_NAME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</span>

                                </th>
                                <th><span onClick={() => toggleOrdering('ADDRESS_STATE')} style={{cursor: 'pointer'}}>State{orderColumn === 'ADDRESS_STATE' && orderDirection === 'DESC' &&
                                    <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                }
                                    {orderColumn === 'ADDRESS_STATE' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</span>

                                </th>
                                <th>Opportunity Stage</th>
                                <th><span onClick={() => toggleOrdering('STATUS')} style={{cursor: 'pointer'}}>Opportunity
                                    Status {orderColumn === 'STATUS' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'STATUS' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</span>

                                </th>
                                <th><span onClick={() => toggleOrdering('ASSIGNED_USER')} style={{cursor: 'pointer'}}>Assigned {orderColumn === 'ASSIGNED_USER' && orderDirection === 'DESC' &&
                                    <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                }
                                    {orderColumn === 'ASSIGNED_USER' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</span>

                                </th>
                                <th><span onClick={() => toggleOrdering('CREATED_DATETIME')} style={{cursor: 'pointer'}}>Created
                                    Date {orderColumn === 'CREATED_DATETIME' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'CREATED_DATETIME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</span>

                                </th>
                                <th><span onClick={() => toggleOrdering('LAST_UPDATED_DATETIME')} style={{cursor: 'pointer'}}>Last
                                    Updated {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'DESC' &&
                                        <IonIcon md={arrowDownOutline} ios={arrowDownSharp}></IonIcon>
                                    }
                                    {orderColumn === 'LAST_UPDATED_DATETIME' && orderDirection === 'ASC' &&
                                        <IonIcon md={arrowUpOutline} ios={arrowUpSharp}></IonIcon>
                                    }</span>

                                </th>
                                <th>Actions</th>
                            </tr>

                            <tr>
                                <th></th>
                                <th><input className={filterColumn === 'FIRST_NAME' ? "aca-filter-active" : "aca-filter"} value={filterColumn === 'FIRST_NAME' ? filterValue : ''} onChange={(e) => handleFilter('FIRST_NAME', e.currentTarget.value)}></input></th>
                                <th><input className={filterColumn === 'LAST_NAME' ? "aca-filter-active" : "aca-filter"} value={filterColumn === 'LAST_NAME' ? filterValue : ''} onChange={(e) => handleFilter('LAST_NAME', e.currentTarget.value)}></input></th>
                                <th><input className={filterColumn === 'ADDRESS_STATE' ? "aca-filter-active" : "aca-filter"} value={filterColumn === 'ADDRESS_STATE' ? filterValue : ''} onChange={(e) => handleFilter('ADDRESS_STATE', e.currentTarget.value)}></input></th>
                                <th></th>
                                <th><input className={filterColumn === 'STATUS' ? "aca-filter-active" : "aca-filter"} value={filterColumn === 'STATUS' ? filterValue : ''} onChange={(e) => handleFilter('STATUS', e.currentTarget.value)}></input></th>
                                <th><input className={filterColumn === 'ASSIGNED_USER' ? "aca-filter-active" : "aca-filter"} value={filterColumn === 'ASSIGNED_USER' ? filterValue : ''} onChange={(e) => handleFilter('ASSIGNED_USER', e.currentTarget.value)}></input></th>
                                <th><input className={filterColumn === 'CREATED_DATETIME' ? "aca-filter-active" : "aca-filter"} value={filterColumn === 'CREATED_DATETIME' ? filterValue : ''} onChange={(e) => handleFilter('CREATED_DATETIME', e.currentTarget.value)}></input></th>
                                <th><input className={filterColumn === 'LAST_UPDATED_DATETIME' ? "aca-filter-active" : "aca-filter"} value={filterColumn === 'LAST_UPDATED_DATETIME' ? filterValue : ''} onChange={(e) => handleFilter('LAST_UPDATED_DATETIME', e.currentTarget.value)}></input></th>

                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {opportunities && opportunities.map((opp, index) => {
                                return (
                                    <tr key={'opp_' + opp.opportunityId}>
                                        <td style={{textAlign: 'center'}}>{opp.opportunityId}</td>
                                        <td><a href={"/aca/view-opportunity/" + opp.opportunityId}>{opp.firstName}</a>
                                        </td>
                                        <td><a href={"/aca/view-opportunity/" + opp.opportunityId}>{opp.lastName}</a></td>
                                        <td style={{textAlign: 'center'}}>{opp.addressState}</td>
                                        <td style={{textAlign: 'center'}}>{opp.stage}</td>
                                        <td style={{textAlign: 'center'}}>{formatStatus(opp.opportunityStatus)}</td>
                                        <td style={{textAlign: 'center'}}>{opp.assignedUserName}</td>
                                        <td style={{textAlign: 'center'}}>{formatDatetime(opp.createdDatetime)}</td>
                                        <td style={{textAlign: 'center'}}>{formatDatetime(opp.lastUpdatedDatetime)}</td>
                                        <td style={{textAlign: 'center'}}>

                                            <IonButton size="small" className="xs-button" fill="clear" routerLink={'/aca/view-opportunity/' + opp.opportunityId} title="Opportunity Details"><IonIcon md={documentOutline} ios={documentSharp}></IonIcon></IonButton>&nbsp;

                                        </td>

                                    </tr>
                                );
                            })}

                            </tbody>
                        </table>
                            {numPages &&
                                <div style={{float:'right', display: 'inline-block'}}><PagingButtons totalPages={numPages} currentPage={currentPage} onPageChange={changePage}></PagingButtons></div>
                            }
                        </>
                    }
                </div>
                <div style={{height: '80px'}}></div>
            </IonContent>
        </IonPage>
    );
};


/** modals **/




export default AcaPipeline;